<script setup lang="ts">
import { nextTick, ref, watch } from 'vue'

import { BoxComponent, DialogModal, FlexComponent, LoadingComponent, NoteComponent, TypographyComponent } from '../..'
import type { AffiliateLinkMetrics, ContentItemLinkDetails } from '../../../types'
import { ContentItemStatusActionEnum } from '../../../types'

import { useActiveContentItem, useContentItemActions, useDetailsHotkeys, useMediaCarousel } from './composables'
import type { ContentItemDownloadRequest, ViewMode } from './content-item-details.types'
import { ContentDataSection, ContentItemMedia, DetailsFooter, DetailsNavArrows, OrdersCostsSection, OverviewSection } from './details'

const props = defineProps<{
  data: ContentItemLinkDetails | null
  contentItemId: string | null
  viewMode: ViewMode
  isLoading?: boolean
  hasError?: boolean
  isDownloading?: boolean
  loadingAction?: ContentItemStatusActionEnum | null
  hideCosts?: boolean
  hideOrders?: boolean
  affiliateLinkMetrics?: AffiliateLinkMetrics
  hasPrevItem?: boolean
  hasNextItem?: boolean
}>()

const emits = defineEmits<{
  (event: 'close'): void
  (event: 'downloadContent', request: ContentItemDownloadRequest): void
  (event: 'openAddCampaignModal'): void
  (event: 'openRemoveCampaignModal', campaignId: number): void
  (event: 'contentItemChange', contentItemId: string): void
  (event: 'approveContent', contentItemId: string): void
  (event: 'rejectContent', contentItemId: string): void
  (event: 'navigate', direction: 'prev' | 'next'): void
}>()

const { activeContentItem, contentItems, captionSegments, setActiveContentItem } = useActiveContentItem({
  data: () => props.data,
  contentItemId: () => props.contentItemId,
  onChange: (id) => emits('contentItemChange', id),
})

const { activeAssetIndex, canPrevAsset, canNextAsset, goToAsset } = useMediaCarousel({
  contentItems,
  activeContentItem,
  onSelect: setActiveContentItem,
})

const { handleAction, actionButtons } = useContentItemActions({
  activeContentItem,
  viewMode: () => props.viewMode,
  loadingAction: () => props.loadingAction,
  isDownloading: () => props.isDownloading,
  onApprove: (id) => emits('approveContent', id),
  onReject: (id) => emits('rejectContent', id),
  onDownload: (request) => emits('downloadContent', request),
})

const captionRef = ref<{ $el: HTMLElement } | null>(null)
const isCaptionExpanded = ref(false)
const isCaptionOverflowing = ref(false)

const measureCaption = () => {
  const el = captionRef.value?.$el
  isCaptionOverflowing.value = el ? el.scrollHeight > el.clientHeight + 1 : false
}

watch(
  captionSegments,
  () => {
    isCaptionExpanded.value = false
    isCaptionOverflowing.value = false
    void nextTick(measureCaption)
  },
  { immediate: true },
)

const navigate = (direction: 'prev' | 'next') => {
  if ((direction === 'next' && props.hasNextItem) || (direction === 'prev' && props.hasPrevItem)) {
    emits('navigate', direction)
  }
}

useDetailsHotkeys({
  onApprove: () => handleAction(ContentItemStatusActionEnum.Approve),
  onReject: () => handleAction(ContentItemStatusActionEnum.Reject),
  onDownload: () => handleAction('download'),
  onNavigate: navigate,
  onStepAsset: goToAsset,
})
</script>
<template>
  <DialogModal
    align-center
    destroy-on-close
    width="var(--cid-modal-width)"
    size="100%"
    class="content-item-details"
    :before-close="() => emits('close')"
  >
    <div class="content-item-details__body">
      <LoadingComponent v-if="isLoading" :size="48" />
      <div v-else-if="data && contentItemId && activeContentItem" class="content-item-details__layout">
        <ContentItemMedia
          :active-content-item="activeContentItem"
          :content-items="contentItems"
          :active-asset-index="activeAssetIndex"
          :can-prev-asset="canPrevAsset"
          :can-next-asset="canNextAsset"
          @prev-asset="goToAsset(-1)"
          @next-asset="goToAsset(1)"
        />
        <FlexComponent direction="column" class="content-item-details__panel">
          <BoxComponent class="content-item-details__sections">
            <BoxComponent :mb="24">
              <TypographyComponent variant="headline-medium">Content details</TypographyComponent>
            </BoxComponent>
            <BoxComponent v-if="captionSegments.length" :mb="24" class="content-item-details__caption">
              <TypographyComponent
                ref="captionRef"
                variant="body-medium"
                class="content-item-details__caption-text"
                :class="{ 'content-item-details__caption-text--clamped': !isCaptionExpanded }"
              >
                <span
                  v-for="(segment, index) in captionSegments"
                  :key="index"
                  :class="{ 'content-item-details__hashtag': segment.isTag }"
                  >{{ segment.text }}</span
                >
              </TypographyComponent>
              <button
                v-if="isCaptionOverflowing"
                type="button"
                class="content-item-details__read-more"
                @click="isCaptionExpanded = !isCaptionExpanded"
              >
                {{ isCaptionExpanded ? 'Read less' : 'Read more' }}
              </button>
            </BoxComponent>
            <ContentDataSection :active-content-item="activeContentItem" :view-mode="viewMode" />
            <OverviewSection
              :data="data"
              :view-mode="viewMode"
              :affiliate-link-metrics="affiliateLinkMetrics"
              @open-add-campaign-modal="emits('openAddCampaignModal')"
              @open-remove-campaign-modal="emits('openRemoveCampaignModal', $event)"
            />
            <OrdersCostsSection
              :data="data"
              :view-mode="viewMode"
              :hide-costs="hideCosts"
              :hide-orders="hideOrders"
              :affiliate-link-metrics="affiliateLinkMetrics"
            />
          </BoxComponent>
          <DetailsFooter :action-buttons="actionButtons" @action="handleAction" />
        </FlexComponent>
      </div>
      <NoteComponent v-else-if="hasError"> Failed to load content item details. </NoteComponent>
    </div>
  </DialogModal>
  <DetailsNavArrows
    :visible="Boolean(data && contentItemId)"
    :has-prev-item="hasPrevItem"
    :has-next-item="hasNextItem"
    @navigate="navigate"
  />
</template>
<style scoped lang="scss">
.content-item-details__body {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: var(--cid-media-height);
  @media screen and (max-width: 991px) {
    height: 100%;
  }
}
.content-item-details__layout {
  display: flex;
  width: 100%;
  height: 100%;
  @media screen and (max-width: 991px) {
    flex-direction: column;
    width: 100%;
    max-width: 100%;
    overflow-x: hidden;
  }
}
.content-item-details__panel {
  flex-shrink: 0;
  box-sizing: border-box;
  width: 440px;
  height: 100%;
  overflow: hidden;
  @media screen and (max-width: 991px) {
    width: 100%;
    height: auto;
    flex: 1 1 auto;
    min-height: 0;
  }
}
.content-item-details__sections {
  box-sizing: border-box;
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 24px;
}
.content-item-details__caption {
  padding: 16px;
  border-radius: 8px;
  background: rgba(34, 34, 34, 0.04);
  white-space: pre-wrap;
  overflow-wrap: anywhere;
}
.content-item-details__caption-text {
  margin: 0;
}
.content-item-details__caption-text--clamped {
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 3;
  overflow: hidden;
}
.content-item-details__read-more {
  margin-top: 8px;
  padding: 0;
  border: none;
  background: none;
  color: var(--primary);
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
}
.content-item-details__hashtag {
  font-weight: 600;
}
</style>
<style lang="scss">
.content-item-details.el-dialog {
  --cid-media-height: min(calc(100vh - 128px), calc((92vw - 440px) / 0.5625));
  --cid-media-width: calc(var(--cid-media-height) * 0.5625);
  --cid-modal-width: calc(var(--cid-media-width) + 440px);
}
.content-item-details.el-dialog,
.content-item-details.el-drawer {
  padding: 0;
  overflow: hidden;
}
.content-item-details .el-dialog__header,
.content-item-details .el-drawer__header {
  padding: 0;
  margin: 0;
}
.content-item-details .el-drawer__body {
  padding: 0;
}
</style>
